<template>
  <div class="connexion">
    <!-- Panneau de marque : un mur d'exposition, dont un cadre porte un point
         de navigation, comme ceux que l'on pose dans l'application. -->
    <aside class="connexion__marque" aria-hidden="true">
      <img :src="logo" alt="" class="connexion__logo" />
      <div class="connexion__mur">
        <span class="cadre cadre--1"></span>
        <span class="cadre cadre--2"></span>
        <span class="cadre cadre--3 cadre--actif">
          <span class="cadre__point"></span>
        </span>
        <span class="cadre cadre--4"></span>
        <span class="cadre cadre--5"></span>
      </div>
      <div class="connexion__accroche">
        <p class="connexion__nom">LogExpo</p>
        <p class="connexion__slogan">
          Concevez et publiez les expositions virtuelles de votre bibliothèque.
        </p>
      </div>
    </aside>

    <main class="connexion__zone">
      <form class="connexion__formulaire" novalidate @submit.prevent="seConnecter">
        <h1 class="connexion__titre">Connexion</h1>
        <p class="connexion__intro">Accédez à l'espace de gestion de vos expositions.</p>

        <label class="connexion__libelle" for="connexion-email">Adresse e-mail</label>
        <q-input
          v-model.trim="email"
          for="connexion-email"
          type="email"
          outlined
          dense
          autofocus
          autocomplete="username"
          inputmode="email"
          placeholder="prenom.nom@universita.corsica"
          class="q-mb-md"
          @update:model-value="effacerMessages"
        />

        <div class="connexion__ligne-libelle">
          <label class="connexion__libelle" for="connexion-mdp">Mot de passe</label>
          <button type="button" class="connexion__lien" @click="reinitialiser">
            Mot de passe oublié ?
          </button>
        </div>
        <q-input
          v-model="motDePasse"
          for="connexion-mdp"
          :type="mdpVisible ? 'text' : 'password'"
          outlined
          dense
          autocomplete="current-password"
          @update:model-value="effacerMessages"
          @keydown="verifierMajuscules"
          @keyup="verifierMajuscules"
          @blur="majuscules = false"
        >
          <template #append>
            <q-icon
              :name="mdpVisible ? 'visibility_off' : 'visibility'"
              class="cursor-pointer"
              role="button"
              tabindex="0"
              :aria-label="mdpVisible ? 'Masquer le mot de passe' : 'Afficher le mot de passe'"
              @click="mdpVisible = !mdpVisible"
              @keydown.enter.prevent="mdpVisible = !mdpVisible"
              @keydown.space.prevent="mdpVisible = !mdpVisible"
            />
          </template>
        </q-input>
        <p v-if="majuscules" class="connexion__avertissement">Majuscules verrouillées</p>

        <q-btn
          type="submit"
          unelevated
          size="lg"
          class="full-width connexion__bouton"
          label="Se connecter"
          :loading="enCours"
        />

        <div aria-live="polite" class="connexion__messages">
          <p v-if="erreur" class="connexion__erreur">{{ erreur }}</p>
          <p v-if="information" class="connexion__information">{{ information }}</p>
        </div>
      </form>
    </main>
  </div>
</template>

<script setup>
import { ref } from "vue";
import { useStore } from "vuex";
import { sendPasswordResetEmail } from "firebase/auth";
import { auth } from "../main.js";
import logo from "@/assets/logo.png";

const store = useStore();

const email = ref("");
const motDePasse = ref("");
const mdpVisible = ref(false);
const majuscules = ref(false);
const enCours = ref(false);
const erreur = ref("");
const information = ref("");

// Codes Firebase (et le nôtre, posé par signInAction) → message affiché.
// « Identifiants incorrects » ne dit pas lequel des deux est faux : c'est
// voulu, pour ne pas révéler quelles adresses ont un compte.
const MESSAGES = {
  "auth/invalid-credential": "Adresse e-mail ou mot de passe incorrect.",
  "auth/invalid-login-credentials": "Adresse e-mail ou mot de passe incorrect.",
  "auth/wrong-password": "Adresse e-mail ou mot de passe incorrect.",
  "auth/user-not-found": "Adresse e-mail ou mot de passe incorrect.",
  "auth/invalid-email": "Cette adresse e-mail n'est pas valide.",
  "auth/user-disabled": "Ce compte est désactivé. Contactez l'administrateur.",
  "auth/too-many-requests":
    "Trop de tentatives. Patientez quelques minutes, ou réinitialisez votre mot de passe.",
  "auth/network-request-failed": "Connexion impossible. Vérifiez votre réseau, puis réessayez.",
  "logexpo/utilisateur-inconnu":
    "Ce compte n'est rattaché à aucun utilisateur LogExpo. Contactez l'administrateur.",
};

function messagePour(e) {
  if (e && MESSAGES[e.code]) return MESSAGES[e.code];
  // Firebase a accepté la connexion, mais notre API n'a pas répondu.
  if (e && e.isAxiosError) return "Le serveur LogExpo ne répond pas. Réessayez dans un instant.";
  return "La connexion a échoué. Réessayez.";
}

function effacerMessages() {
  erreur.value = "";
  information.value = "";
}

function verifierMajuscules(event) {
  if (typeof event.getModifierState === "function") {
    majuscules.value = event.getModifierState("CapsLock");
  }
}

async function seConnecter() {
  effacerMessages();
  if (!email.value || !motDePasse.value) {
    erreur.value = "Saisissez votre adresse e-mail et votre mot de passe.";
    return;
  }
  enCours.value = true;
  try {
    await store.dispatch("signInAction", { email: email.value, password: motDePasse.value });
  } catch (e) {
    erreur.value = messagePour(e);
  } finally {
    enCours.value = false;
  }
}

async function reinitialiser() {
  effacerMessages();
  if (!email.value) {
    erreur.value = "Saisissez d'abord votre adresse e-mail, puis cliquez à nouveau sur « Mot de passe oublié ? ».";
    return;
  }
  // Même message qu'un compte existe ou non : la page ne doit pas servir à
  // tester quelles adresses sont inscrites.
  const confirmation =
    "Si un compte existe pour cette adresse, un e-mail de réinitialisation vient de lui être envoyé.";
  auth.languageCode = "fr";
  try {
    await sendPasswordResetEmail(auth, email.value);
    information.value = confirmation;
  } catch (e) {
    if (e.code === "auth/user-not-found") information.value = confirmation;
    else erreur.value = messagePour(e);
  }
}
</script>

<style scoped>
.connexion {
  --violet: #8257d4;
  --violet-fonce: #6a41bd;
  --violet-clair: #cbb8f0;
  min-height: 100vh;
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr);
  background: #fff;
}

/* ---- Panneau de marque ---- */
.connexion__marque {
  position: relative;
  overflow: hidden;
  background: var(--violet);
  color: #fff;
  padding: 40px 48px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}
.connexion__logo {
  width: 84px;
  height: 84px;
  /* Logo noir sur fond blanc : inversé puis fondu en « screen », le blanc
     devient transparent et le trait du lama ressort en blanc sur le violet. */
  filter: invert(1);
  mix-blend-mode: screen;
}
.connexion__mur {
  position: relative;
  flex: 1;
  margin: 32px 0;
  max-width: 560px;
  width: 100%;
  align-self: center;
}
.cadre {
  position: absolute;
  border: 1.5px solid var(--violet-clair);
  border-radius: 2px;
  animation: apparition 0.8s ease-out both;
}
.cadre--1 { left: 4%;  top: 8%;  width: 34%; height: 44%; animation-delay: 0.05s; }
.cadre--2 { left: 44%; top: 2%;  width: 24%; height: 28%; animation-delay: 0.15s; }
.cadre--3 { left: 44%; top: 36%; width: 24%; height: 34%; animation-delay: 0.25s; }
.cadre--4 { left: 74%; top: 12%; width: 22%; height: 54%; animation-delay: 0.35s; }
.cadre--5 { left: 10%; top: 60%; width: 26%; height: 26%; animation-delay: 0.45s; }
.cadre--actif {
  border-color: #fff;
  background: rgba(255, 255, 255, 0.12);
}
.cadre__point {
  position: absolute;
  left: 50%;
  top: 58%;
  width: 10px;
  height: 10px;
  margin: -5px 0 0 -5px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.6);
  animation: pulsation 2.4s ease-out 1s infinite;
}
.connexion__nom {
  font-size: 34px;
  font-weight: 700;
  line-height: 1.15;
  margin: 0;
}
.connexion__slogan {
  font-size: 16px;
  line-height: 1.5;
  color: #ece6fb;
  margin: 8px 0 0;
  max-width: 420px;
}

/* ---- Formulaire ---- */
.connexion__zone {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 32px;
}
.connexion__formulaire {
  width: 100%;
  max-width: 380px;
  animation: montee 0.5s ease-out both;
}
.connexion__titre {
  font-size: 28px;
  font-weight: 700;
  line-height: 1.2;
  margin: 0;
  color: #1d1d1d;
}
.connexion__intro {
  font-size: 14px;
  color: #666;
  margin: 6px 0 28px;
}
.connexion__libelle {
  display: block;
  font-size: 13px;
  color: #444;
  margin-bottom: 6px;
}
.connexion__ligne-libelle {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
}
.connexion__lien {
  background: none;
  border: 0;
  padding: 0;
  font: inherit;
  font-size: 13px;
  color: var(--violet);
  cursor: pointer;
}
.connexion__lien:hover { text-decoration: underline; }
.connexion__lien:focus-visible,
.connexion__formulaire :deep(.q-icon:focus-visible) {
  outline: 2px solid var(--violet);
  outline-offset: 2px;
  border-radius: 2px;
}
.connexion__formulaire :deep(.q-field--outlined.q-field--focused .q-field__control:after) {
  border-color: var(--violet);
}
.connexion__avertissement {
  font-size: 12px;
  color: #8a5a00;
  margin: 6px 0 0;
}
.connexion__bouton {
  margin-top: 24px;
  background: var(--violet);
  color: #fff;
}
.connexion__bouton:hover { background: var(--violet-fonce); }
.connexion__messages p {
  font-size: 14px;
  line-height: 1.45;
  margin: 14px 0 0;
}
.connexion__erreur { color: #b3261e; }
.connexion__information { color: #1b5e20; }

/* ---- Mobile : le panneau devient un bandeau ---- */
@media (max-width: 900px) {
  .connexion {
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr;
  }
  .connexion__marque {
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    gap: 16px;
    padding: 20px 24px;
  }
  .connexion__logo { width: 56px; height: 56px; }
  .connexion__mur { display: none; }
  .connexion__nom { font-size: 24px; }
  .connexion__slogan { font-size: 13px; margin-top: 2px; }
  .connexion__zone { align-items: flex-start; padding: 32px 24px; }
}

@keyframes apparition {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: none; }
}
@keyframes montee {
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: none; }
}
@keyframes pulsation {
  0% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.6); }
  70% { box-shadow: 0 0 0 14px rgba(255, 255, 255, 0); }
  100% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0); }
}
@media (prefers-reduced-motion: reduce) {
  .cadre, .cadre__point, .connexion__formulaire { animation: none; }
}
</style>

<style>
/* ⚠️ Règle globale héritée de l'ancienne page de connexion. Login.vue est
   chargé au démarrage : cette règle s'applique donc à TOUTES les q-card de
   l'application, et certaines pages en dépendent sans doute. Elle est gardée
   telle quelle pour ne rien déplacer ailleurs ; la retirer demande de passer
   les cartes des autres pages en revue. */
.q-card {
  width: 360px;
}
</style>
